import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  CloudArrowUpIcon, 
  DocumentArrowUpIcon, 
  EyeIcon, 
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { formatDate, formatNumber } from '../utils/format';
import { veiculoService } from '../services/api';

const CRLVUpload = ({ veiculoId, crlvData, onUpdate }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Monitorar mudanças no estado do modal
  useEffect(() => {
    console.log('🔄 Modal state changed:', showModal);
    if (showModal) {
      console.log('🎭 Modal ABERTO');
    } else {
      console.log('🎭 Modal FECHADO');
    }
  }, [showModal]);

  // Monitorar mudanças nos dados do CRLV
  useEffect(() => {
    console.log('🔄 CRLV data changed:', crlvData);
    if (crlvData && crlvData.nome && crlvData.nome.trim() !== '') {
      console.log('✅ CRLV data válido - modal pode ser aberto');
    } else {
      console.log('❌ CRLV data inválido - modal não pode ser aberto');
    }
  }, [crlvData]);

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }) => {
      console.log('🚀 ===== INÍCIO UPLOAD FRONTEND =====');
      console.log('📤 Enviando arquivo:', file.name);
      console.log('📤 Veículo ID:', id);
      return veiculoService.uploadCRLV(id, file);
    },
    onSuccess: (response) => {
      console.log('✅ Upload CRLV - Sucesso:', response);
      console.log('✅ Response data:', response.data);
      console.log('✅ Response data.data:', response.data?.data);
      console.log('✅ Response data.data.arquivo:', response.data?.data?.arquivo);
      toast.success('CRLV enviado com sucesso!');
      
      if (onUpdate && response.data?.data?.arquivo) {
        console.log('🔄 Chamando onUpdate com arquivo:', response.data.data.arquivo);
        console.log('🔄 Estado do modal antes do onUpdate:', showModal);
        onUpdate(response.data.data.arquivo);
        console.log('🔄 Estado do modal após onUpdate:', showModal);
      } else {
        console.log('⚠️ onUpdate não chamado - dados inválidos');
        console.log('⚠️ onUpdate existe?', !!onUpdate);
        console.log('⚠️ response.data?.data?.arquivo existe?', !!response.data?.data?.arquivo);
      }
      console.log('🚀 ===== FIM UPLOAD FRONTEND =====');
    },
    onError: (error) => {
      console.error('❌ Upload CRLV - Erro:', error);
      console.error('❌ Error response:', error.response);
      toast.error(error.response?.data?.message || 'Erro ao enviar CRLV');
    }
  });

  const removeMutation = useMutation({
    mutationFn: (id) => veiculoService.removerCRLV(id),
    onSuccess: () => {
      toast.success('CRLV removido com sucesso!');
      if (onUpdate) {
        onUpdate(null);
      }
    },
    onError: (error) => {
      console.error('❌ Remove CRLV - Erro:', error);
      toast.error(error.response?.data?.message || 'Erro ao remover CRLV');
    }
  });

  const downloadMutation = useMutation({
    mutationFn: (id) => {
      console.log('📥 Download mutation - iniciando para ID:', id);
      return veiculoService.downloadCRLV(id);
    },
    onSuccess: (response) => {
      console.log('✅ Download mutation - sucesso');
      // Criar link de download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', crlvData?.nome || 'crlv.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    },
    onError: (error) => {
      console.error('❌ Download mutation - erro:', error);
      toast.error('Erro ao fazer download do CRLV');
    }
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!veiculoId) {
      toast.error('ID do veículo não encontrado');
      return;
    }

    // Validar tipo de arquivo
    const tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!tiposPermitidos.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Apenas PDF, JPG e PNG são aceitos');
      return;
    }

    // Validar tamanho (5MB)
    const tamanhoMaximo = 5 * 1024 * 1024;
    if (file.size > tamanhoMaximo) {
      toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    setUploading(true);
    uploadMutation.mutate(
      { id: veiculoId, file },
      {
        onSettled: () => {
          setUploading(false);
        }
      }
    );
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    if (window.confirm('Tem certeza que deseja remover o CRLV?')) {
      removeMutation.mutate(veiculoId);
    }
  };

  const handleDownload = () => {
    console.log('📥 Iniciando download do CRLV');
    downloadMutation.mutate(veiculoId);
  };

  const getFileIcon = (tipo) => {
    if (tipo?.includes('image')) {
      return '🖼️';
    } else if (tipo?.includes('pdf')) {
      return '📄';
    }
    return '📁';
  };

  console.log('🔍 ===== RENDERIZANDO CRLVUPLOAD =====');
  console.log('🔍 crlvData:', crlvData);
  console.log('🔍 crlvData existe?', !!crlvData);
  console.log('🔍 crlvData.nome:', crlvData?.nome);
  console.log('🔍 crlvData.nome.trim():', crlvData?.nome?.trim());
  console.log('🔍 Condição para mostrar arquivo:', crlvData && crlvData.nome && crlvData.nome.trim() !== '');
  console.log('🔍 Estado do modal:', showModal);
  console.log('🔍 Modal deve aparecer:', showModal && crlvData);
  
  if (crlvData && crlvData.nome && crlvData.nome.trim() !== '') {
    console.log('✅ Mostrando arquivo existente');
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">CRLV</h3>
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('👁️ Abrindo modal de visualização');
                setShowModal(true);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              Visualizar
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🗑️ Botão remover clicado');
                handleRemove();
              }}
              disabled={removeMutation.isPending}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Remover
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-2xl">{getFileIcon(crlvData.tipo)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {crlvData.nome}
            </p>
            <p className="text-sm text-gray-500">
              {formatNumber(crlvData.tamanho)} bytes • {formatDate(crlvData.dataUpload)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  console.log('❌ Mostrando área de upload (arquivo não existe)');
  return (
    <>
      {/* Modal de Visualização */}
      {showModal && crlvData && (
        <>
          {console.log('🎭 RENDERIZANDO MODAL - showModal:', showModal, 'crlvData:', crlvData)}
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              console.log('🔄 Fechando modal - clique fora');
              setShowModal(false);
            }
          }}
        >
          <div 
            className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Visualizar CRLV - {crlvData.nome}
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('📥 Botão download clicado');
                    handleDownload();
                  }}
                  disabled={downloadMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('❌ Fechando modal - botão fechar');
                    setShowModal(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Fechar
                </button>
              </div>
            </div>
            
            <div className="mt-4">
              {crlvData.tipo?.includes('image') ? (
                <img
                  src={`/api/veiculos/${veiculoId}/crlv`}
                  alt="CRLV"
                  className="max-w-full h-auto mx-auto"
                />
              ) : (
                <div className="text-center py-8">
                  <DocumentArrowUpIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Arquivo PDF - {crlvData.nome}</p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('📥 Botão download PDF clicado');
                      handleDownload();
                    }}
                    disabled={downloadMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                    Baixar PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {/* Upload Area */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">CRLV</h3>
        </div>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
            dragActive ? 'border-purple-400 bg-purple-50' : 'border-gray-300'
          } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          
          <CloudArrowUpIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          
          {uploading ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Enviando arquivo...</p>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Arraste e solte o arquivo CRLV aqui, ou clique para selecionar
              </p>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG até 5MB
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CRLVUpload;